import type { TFunction } from 'i18next';
import type { User } from '../types';

/**
 * The flat side menu, proposal 1 (ClickUp 869fdak39): one level of menu items
 * per role (admin under headings, never folding), a primary button on top and
 * Support / Inställningar at the bottom. A page's sibling pages (e.g.
 * Projektlista / Avslutade) are tabs on the page, not menu items. Pages and
 * routes are unchanged.
 */
export interface NavLink {
  to: string;
  label: string;
  icon: string;
  disabled?: boolean;
  /** A dot that asks for attention (e.g. company profile incomplete). */
  attention?: boolean;
  /** A count that asks for attention, e.g. unread messages (ClickUp 869fegbvk, 869fj3q97). */
  badge?: number;
}

export interface NavEntry extends NavLink {
  id: string;
  /** Sibling pages, shown as tabs on each of them. */
  tabs?: NavLink[];
  /** Other paths (and their subpaths) that belong here, e.g. detail pages. */
  match?: string[];
  /** Not in the menu; only gives its pages their tabs. */
  hidden?: boolean;
  /** In the phone's bottom bar, so left out of its "Mer" menu (ClickUp 869fmfrmm). */
  inBottomNav?: boolean;
}

export interface NavSection {
  /** Admin's headings; the other roles have one section without one. */
  heading?: string;
  entries: NavEntry[];
}

export interface AppNavigation {
  home: string;
  /** The role's main action, e.g. "+ Skapa projekt". */
  primary: NavLink | null;
  sections: NavSection[];
  /** Support and Inställningar, at the bottom of the menu. */
  bottom: NavLink[];
  /** Account pages: only in the avatar menu. */
  account: NavLink[];
  notificationsPath: string;
  /** The bar along the bottom edge on phones (field roles), or null to keep the menu button. */
  mobile: MobileNavItem[] | null;
}

/**
 * One place in the phone's bottom bar: a page (optionally with a count, e.g.
 * unread messages), the home page's "Att göra nu" with its count, or "Mer"
 * which opens the full menu.
 */
export type MobileNavItem =
  | { kind: 'link'; id: string; to: string; label: string; icon: string; badge?: number }
  | { kind: 'todo'; to: string; label: string; icon: string }
  | { kind: 'more'; label: string; icon: string };

/** Where "Att göra" in the bottom bar goes: the "Att göra nu" panel on the home page. */
export const TODO_ANCHOR = 'att-gora';

type MenuUser = Pick<User, 'isAdmin' | 'isSupport' | 'personaType'>;

interface Options {
  companyProfileAttention?: boolean;
  /** Unread messages, shown next to Meddelanden. */
  unreadMessages?: number;
  debugConfigurable?: boolean;
}

const link = (to: string, label: string, icon: string, extra: Partial<NavLink> = {}): NavLink => ({ to, label, icon, ...extra });
const entry = (id: string, to: string, label: string, icon: string, extra: Partial<NavEntry> = {}): NavEntry => ({ id, to, label, icon, ...extra });

/** The menu for a signed-in user, or null before anyone is signed in. */
export function buildAppNavigation(user: MenuUser | null | undefined, t: TFunction, options: Options = {}): AppNavigation | null {
  if (!user) return null;
  const support = link('/kontakt', t('appNav.support'), 'help-circle');
  // The parties' milestone conversations, one page for both (ClickUp 869fegbvk, 869feg849).
  const messages = entry('messages', '/messages', t('appNav.messages'), 'message-square', {
    badge: options.unreadMessages || undefined,
  });

  // Support staff only have their profile.
  if (user.isSupport) {
    const profile = link('/admin/profile', t('appNav.profile'), 'user');
    return {
      home: '/admin/profile',
      primary: null,
      notificationsPath: '/notifications',
      mobile: null,
      bottom: [],
      account: [profile],
      sections: [{ entries: [entry('profile', profile.to, profile.label, profile.icon)] }],
    };
  }

  if (user.isAdmin) {
    return {
      home: '/admin/home',
      primary: null,
      notificationsPath: '/admin/notifications?tab=log',
      mobile: null,
      bottom: [link('/admin/profile', t('appNav.settings'), 'settings')],
      account: [link('/admin/profile', t('appNav.profile'), 'user')],
      sections: [
        { entries: [entry('home', '/admin/home', t('appNav.home'), 'home')] },
        {
          heading: t('appNav.users'),
          entries: [
            entry('companies', '/admin/company-list', t('appNav.companies'), 'briefcase', {
              tabs: [link('/admin/company-list', t('appNav.allCompanies'), 'list'), link('/admin/company-invitations', t('appNav.invitations'), 'mail'), link('/admin/company-registrations', t('appNav.registrations'), 'bar-chart-2')],
              match: ['/admin/companies'],
            }),
            entry('customers', '/admin/customers', t('appNav.customers'), 'users', {
              tabs: [link('/admin/customers', t('appNav.allCustomers'), 'list'), link('/admin/customer-invitations', t('appNav.invitations'), 'mail'), link('/admin/customer-registrations', t('appNav.registrations'), 'bar-chart-2')]
            }),
            entry('experts', '/admin/lawyers', t('appNav.experts'), 'award', {
              tabs: [
                link('/marketplace', t('appNav.findHelp'), 'search'),
                link('/admin/lawyers', t('appNav.lawyers'), 'briefcase'),
                link('/admin/inspectors', t('appNav.inspectors'), 'clipboard'),
              ],
            }),
          ],
        },
        {
          heading: t('appNav.operations'),
          entries: [
            entry('projects', '/admin/projects', t('appNav.projects'), 'folder', {
              tabs: [link('/admin/projects', t('appNav.projectList'), 'list'), link('/admin/projects/completed', t('appNav.completed'), 'check-circle')],
            }),
            entry('review', '/admin/reports/contract-review', t('appNav.review'), 'shield', {
              tabs: [
                link('/admin/reports/contract-review', t('appNav.contractReview'), 'file-text'),
                link('/admin/reports/lawyer-requests', t('appNav.lawyerRequests'), 'briefcase'),
                link('/admin/reports/inspector-requests', t('appNav.inspectorRequests'), 'clipboard'),
              ],
            }),
          ],
        },
        {
          heading: t('appNav.money'),
          entries: [
            entry('transactions', '/admin/transactions', t('appNav.transactions'), 'list', {
              tabs: [link('/admin/transactions', t('appNav.transactions'), 'list'), link('/admin/transactions/refund-policy', t('appNav.refundPolicy'), 'sliders')],
            }),
            entry('ping', '/admin/ping-webhooks', t('adminPingWebhooks.title'), 'activity'),
            entry('accounting', '/admin/accounting', t('appNav.accounting'), 'book'),
            entry('adyen', '/admin/adyen-payment-methods', t('appNav.adyen'), 'shield', {
              tabs: [
                link('/admin/adyen-payment-methods', t('appNav.paymentMethods'), 'toggle-right'),
                link('/admin/adyen-ledger', t('appNav.ledger'), 'file-text'),
                link('/admin/adyen-accounts', t('appNav.accounts'), 'shield'),
                link('/admin/adyen-webhook-logs', t('appNav.webhookLog'), 'alert-triangle'),
              ],
            }),
          ],
        },
        {
          heading: t('appNav.contentAndSystem'),
          entries: [
            entry('documents', '/admin/documents/library', t('appNav.documents'), 'book-open', {
              tabs: [link('/admin/documents/library', t('appNav.library'), 'book-open'), link('/admin/documents/agreements', t('appNav.agreements'), 'file-text')],
            }),
            entry('configuration', '/admin/system/contract-config', t('appNav.configuration'), 'sliders', {
              tabs: [
                link('/admin/system/contract-config', t('appNav.contractConfig'), 'sliders'),
                link('/admin/system/project-config', t('appNav.projectConfig'), 'git-merge'),
                link('/admin/system/kpi-defaults', t('appNav.kpiDefaults'), 'bar-chart-2'),
                link('/admin/system/conflict-settings', t('appNav.conflictSettings'), 'clock'),
                link('/admin/system/session-settings', t('adminSessionSettings.title'), 'lock'),
                // The banks a Beställare chooses among (ClickUp 869eu6fpm).
                link('/admin/system/banks', t('appNav.banks'), 'credit-card'),
                link('/admin/system/testers', t('adminDebugSettings.stagingTesters.title'), 'users'),
                link('/admin/system/debug', t('appNav.debug'), 'tool', { disabled: options.debugConfigurable === false }),
              ],
              match: ['/admin/debug'],
            }),
            // Definitions, log, deliveries and template preview (ClickUp 869fkva4j).
            entry('notifications', '/admin/notifications', t('appNav.notifications'), 'bell'),
            entry('reports', '/admin/reports/terms-acceptances', t('appNav.reports'), 'bar-chart-2', {
              tabs: [
                link('/admin/reports/terms-acceptances', t('appNav.termsAcceptances'), 'file-text'),
                link('/admin/reports/audit-trail', t('appNav.auditTrail'), 'activity'),
              ],
            }),
            entry('admins', '/admin/admin-list', t('appNav.admins'), 'user-check'),
            entry('system', '/admin/system/company-info', t('appNav.systemInfo'), 'info', {
              tabs: [link('/admin/system/company-info', t('appNav.information'), 'info'), link('/admin/system/versions', t('appNav.versions'), 'git-commit')],
            }),
          ],
        },
      ],
    };
  }

  const home = entry('home', '/home', t('appNav.home'), 'home');
  // Which notifications also come by e-mail (ClickUp 869fkva3u).
  const notificationSettings = link('/settings/notifications', t('appNav.notificationSettings'), 'bell');
  const mobileHome: MobileNavItem = { kind: 'link', id: 'home', to: '/home', label: t('appNav.home'), icon: 'home' };
  const mobileTodo: MobileNavItem = { kind: 'todo', to: `/home#${TODO_ANCHOR}`, label: t('appNav.todo'), icon: 'check-circle' };
  const mobileMore: MobileNavItem = { kind: 'more', label: t('appNav.more'), icon: 'menu' };
  // Beställare and Hantverkare have Meddelanden in the bottom bar (ClickUp 869fmfrmm).
  const mobileMessages: MobileNavItem = { kind: 'link', id: 'messages', to: messages.to, label: messages.label, icon: messages.icon, badge: messages.badge };
  const fieldMessages: NavEntry = { ...messages, inBottomNav: true };

  if (user.personaType === 'person') {
    return {
      home: '/home',
      primary: link('/invite', t('appNav.inviteCompany'), 'send'),
      notificationsPath: '/notifications',
      bottom: [support, link('/profile', t('appNav.settings'), 'settings')],
      account: [link('/profile', t('appNav.profile'), 'user'), notificationSettings],
      mobile: [
        mobileHome,
        { kind: 'link', id: 'projects', to: '/person/projects', label: t('appNav.projects'), icon: 'folder' },
        mobileMessages,
        mobileTodo,
        mobileMore,
      ],
      sections: [{
        entries: [
          home,
          entry('projects', '/person/projects', t('appNav.myProjects'), 'folder', {
            tabs: [link('/person/projects', t('appNav.projectList'), 'list'), link('/person/projects/completed', t('appNav.completed'), 'check-circle')],
          }),
          entry('companies', '/my-companies', t('appNav.myCompanies'), 'briefcase', { match: ['/person/companies'] }),
          fieldMessages,
          // One page without tabs: Mina experter and Hitta hjälp (ClickUp 869fh4y1m).
          entry('experts', '/marketplace', t('nav.marketplace'), 'award'),
          entry('invites', '/invite', t('appNav.inviteCompany'), 'send', {
            hidden: true,
            tabs: [link('/invite', t('appNav.invite'), 'send'), link('/invite-status', t('appNav.invitations'), 'list')],
            match: ['/invite/status'],
          }),
        ],
      }],
    };
  }

  if (user.personaType === 'lawyer' || user.personaType === 'inspector') {
    const lawyer = user.personaType === 'lawyer';
    const base = lawyer ? '/lawyer/projects' : '/inspector/projects';
    const settings = lawyer ? '/settings/lawyer' : '/settings/inspector';
    return {
      home: '/home',
      primary: null,
      notificationsPath: '/notifications',
      bottom: [support, link(settings, t('appNav.settings'), 'settings')],
      account: [
        link('/profile', t('appNav.profile'), 'user'),
        link(settings, t(lawyer ? 'appNav.lawyerDetails' : 'appNav.inspectorDetails'), lawyer ? 'briefcase' : 'clipboard'),
        link(`${settings}/marketplace-preview`, t('appNav.marketplaceProfile'), 'eye'),
        notificationSettings,
      ],
      mobile: [
        mobileHome,
        { kind: 'link', id: 'projects', to: base, label: t(lawyer ? 'appNav.cases' : 'appNav.assignments'), icon: 'folder' },
        { kind: 'link', id: 'contacts', to: '/my-companies', label: t('appNav.contacts'), icon: 'users' },
        mobileMore,
      ],
      sections: [{
        entries: [
          home,
          entry('projects', base, t(lawyer ? 'appNav.cases' : 'appNav.assignments'), 'folder', {
            tabs: [link(base, t('appNav.projectList'), 'list'), link(`${base}/completed`, t('appNav.completed'), 'check-circle')],
          }),
          entry('contacts', '/my-companies', t('appNav.contacts'), 'users', {
            tabs: [
              link('/my-companies', t('appNav.companies'), 'briefcase'),
              lawyer ? link('/my-inspectors', t('appNav.inspectors'), 'clipboard') : link('/my-lawyers', t('appNav.lawyers'), 'briefcase'),
            ],
          }),
        ],
      }],
    };
  }

  // Company.
  return {
    home: '/home',
    primary: link('/projects/create', t('appNav.createProject'), 'plus'),
    notificationsPath: '/notifications',
    bottom: [support, link('/settings/company', t('appNav.settings'), 'settings', { attention: options.companyProfileAttention })],
    account: [
      link('/profile', t('appNav.profile'), 'user'),
      link('/settings/company', t('appNav.companyDetails'), 'briefcase', { attention: options.companyProfileAttention }),
      link('/settings/theme', t('appNav.theme'), 'droplet'),
      notificationSettings,
    ],
    mobile: [
      mobileHome,
      { kind: 'link', id: 'projects', to: '/projects', label: t('appNav.projects'), icon: 'folder' },
      mobileMessages,
      mobileTodo,
      mobileMore,
    ],
    sections: [{
      entries: [
        home,
        entry('projects', '/projects', t('appNav.projects'), 'folder', {
          tabs: [link('/projects', t('appNav.projectList'), 'list'), link('/projects/completed', t('appNav.completed'), 'check-circle')],
          match: ['/company/projects'],
        }),
        entry('customers', '/customers', t('appNav.customers'), 'users', {
          tabs: [link('/customers', t('appNav.customers'), 'list'), link('/customers/invite-status', t('appNav.invitations'), 'mail')],
        }),
        entry('economy', '/accounting/company-report', t('appNav.economy'), 'credit-card', { match: ['/accounting'] }),
        fieldMessages,
        entry('experts', '/marketplace', t('nav.marketplace'), 'award'),
      ],
    }],
  };
}

const within = (pathname: string, path: string) => {
  const clean = path.split('?')[0];
  return pathname === clean || pathname.startsWith(`${clean}/`);
};

const entryPaths = (navEntry: NavEntry): string[] =>
  [navEntry.to, ...(navEntry.tabs ?? []).map(tab => tab.to), ...(navEntry.match ?? [])];

/** The menu entry the current page belongs to: the longest matching path wins. */
export function activeEntry(navigation: AppNavigation, pathname: string): NavEntry | null {
  let best: { navEntry: NavEntry; length: number } | null = null;
  for (const section of navigation.sections) {
    for (const navEntry of section.entries) {
      for (const path of entryPaths(navEntry)) {
        const matches = navEntry.id === 'home' ? pathname === path : within(pathname, path);
        if (matches && (!best || path.length > best.length)) best = { navEntry, length: path.length };
      }
    }
  }
  return best?.navEntry ?? null;
}

/** The tabs for the current page: only on one of the tabs' own pages, not on a detail page. */
export function activeTabs(navigation: AppNavigation, pathname: string): NavLink[] {
  const tabs = activeEntry(navigation, pathname)?.tabs ?? [];
  return tabs.length > 1 && tabs.some(tab => tab.to === pathname) ? tabs : [];
}
